import { useEffect } from 'react';
import { canonicalUrl, DEFAULT_OG_IMAGE, SITE_NAME } from '../lib/seo';

// SEO manages document.title, canonical, meta description, OG/Twitter tags
// and any JSON-LD schemas provided. It cleans up injected schemas on unmount.
export default function SEO({
  title,
  description,
  path = '/',
  image = DEFAULT_OG_IMAGE,
  robots = 'index, follow, max-image-preview:large, max-snippet:-1',
  schemas = [],
  ogType = 'website',
}) {
  useEffect(() => {
    if (title) document.title = title;
    const url = canonicalUrl(path);

    const set = (selector, attrs) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement(attrs.__tag || 'meta');
        document.head.appendChild(el);
      }
      Object.entries(attrs).forEach(([k, v]) => {
        if (k === '__tag') return;
        el.setAttribute(k, v);
      });
      return el;
    };

    // Meta description
    if (description) {
      set('meta[name="description"]', { name: 'description', content: description });
    }
    set('meta[name="robots"]', { name: 'robots', content: robots });
    // Canonical
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', url);

    // Open Graph
    set('meta[property="og:title"]', { property: 'og:title', content: title || SITE_NAME });
    set('meta[property="og:description"]', { property: 'og:description', content: description || '' });
    set('meta[property="og:type"]', { property: 'og:type', content: ogType });
    set('meta[property="og:url"]', { property: 'og:url', content: url });
    set('meta[property="og:image"]', { property: 'og:image', content: image });
    set('meta[property="og:site_name"]', { property: 'og:site_name', content: SITE_NAME });
    set('meta[property="og:locale"]', { property: 'og:locale', content: 'en_GB' });

    // Twitter
    set('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    set('meta[name="twitter:title"]', { name: 'twitter:title', content: title || SITE_NAME });
    set('meta[name="twitter:description"]', { name: 'twitter:description', content: description || '' });
    set('meta[name="twitter:image"]', { name: 'twitter:image', content: image });

    // Schemas: inject fresh script tags per page under a dedicated id family
    const CLASS = 'route-jsonld';
    document.head.querySelectorAll(`script.${CLASS}`).forEach(s => s.remove());
    (schemas || []).forEach((s, i) => {
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.className = CLASS;
      script.textContent = JSON.stringify(s);
      document.head.appendChild(script);
    });

    return () => {
      // Do not remove global meta — they'll be overwritten by next route mount
      document.head.querySelectorAll(`script.${CLASS}`).forEach(s => s.remove());
    };
  }, [title, description, path, image, robots, ogType, JSON.stringify(schemas)]);

  return null;
}
